import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Spinner } from 'react-bootstrap';
import useProductApi, { Product } from '../../managers/ProductManager';
import ProductDetail from "./component/ProductDetail";
import { Helmet } from "react-helmet-async";
import useCatalogApi from "../../managers/CatalogManager";

const ProductPage: React.FC = () => {
  const { productId, catalogItemId, category } = useParams<{ catalogItemId: string; category: string; productId: string }>();
  const { getProduct, getProductUrl } = useProductApi();
  const { getCatalogItem } = useCatalogApi();
  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);
  const [productCategory, setProductCategory] = useState<string | undefined>(category);

  useEffect(() => {
    async function fetchProduct() {
      try {
        if (productId) {
          const fetchedProduct = await getProduct(productId);
          setProduct(fetchedProduct);

          // If category not in URL, fetch it from catalog item for proper URL
          if (!category && fetchedProduct.catalogItemId) {
            try {
              const catalogItem = await getCatalogItem(fetchedProduct.catalogItemId);
              setProductCategory(catalogItem?.category);
            } catch (err) {
              console.warn("Could not fetch catalog item for category:", err);
            }
          }
        } else {
          throw new Error("Missing product ID");
        }
      } catch (error) {
        console.error("Error fetching product:", error);
      } finally {
        setLoading(false);
      }
    }
    fetchProduct();
  }, [productId, category]);

  if (loading) return <Spinner animation="border" />;
  if (!product) return <p>Product not found</p>;

  // Generate proper product URL with category for SEO
  const fullProductUrl = getProductUrl(product, productCategory);

  return (
    <>
      <Helmet>
        <title>{`${product.name} | Wallscapes.AI`}</title>
        <meta name="description" content={product.description} />
        <meta property="og:title" content={product.name} />
        <meta property="og:description" content={product.description} />
        {Array.isArray(product.previewImageUrls) && product.previewImageUrls[0] && (
          <meta property="og:image" content={product.previewImageUrls[0]} />
        )}
        <meta property="og:url" content={fullProductUrl} />
      </Helmet>
      <ProductDetail product={product} />
    </>
  );
};

export default ProductPage;
